/* © CNES */
import { useQueries, useQuery } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  deleteSavedProduct,
  restoreArchivedProduct,
  saveSelectedProducts,
} from '../../../../api/cart/Cart';
import { downloadProductZipFile } from '../../../../api/items/Items';
import { getDownloadQuotas } from '../../../../api/process/process';
import { ConfigContext } from '../../../../context/ConfigContext';
import { AssetWithProductId } from '../../../../models/CollectionDetails';
import { ProductsAdded } from '../../../../models/ProductsAdded';
import { DATASET } from '../../../shared/Constants';
import { getTooltipMessage } from '../../../shared/Utils';
import './CollectionActionButtons.scss';
import { ShowFilesToDownloadDialog } from './ShowFilesToDownloadDialog/ShowFilesToDownloadDialog';

/**
 * CollectionActionButtons Props
 */
interface CollectionActionButtonsProps {
  storageMode: boolean | undefined;
  setDownloadProgress: Function;
  isLoading: { showSpinner: boolean; productId: string }[];
  setIsLoading: Function;
  isDatalakeAvailable: string;
  setMaxProductsWarningVisible: Function;
}

/**
 * Collection Action Buttons to Save or download a product.
 * @returns CollectionActionButtons Element.
 */
export const CollectionActionButtons = ({
  storageMode,
  setDownloadProgress,
  isLoading,
  setIsLoading,
  isDatalakeAvailable,
  setMaxProductsWarningVisible,
}: CollectionActionButtonsProps) => {
  const context = useContext(ConfigContext);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [assetsToShow, setAssetsToShow] = useState<AssetWithProductId[]>([]);
  const [assetsToDownload, setAssetsToDownload] = useState<
    AssetWithProductId[]
  >([]);
  const navigate = useNavigate();
  const [isCollectionInMyData, setIsCollectionInMyData] = useState<boolean>(
    context.productsAdded?.products?.includes(
      context.selectedProduct?.productName
    )
  );
  const isAuthDisabled = process.env.NODE_ENV !== 'production';

  const toast = useRef<Toast>(null);

  /**
   * message Display
   * @param numberOfProduct numberOfProducts that are ADDED or REMOVED from cart.
   * @param message what actions are applied to products ex: ADDED or REMOVED
   */
  const messageDisplay = (numberOfProduct: number, message: string) => {
    toast.current?.show({
      severity: 'success',
      summary: 'Success',
      detail: `${numberOfProduct} ${message}`,
      life: 2000,
    });
  };
  /**
   * Change button status when the displayed product is already Added.
   */
  useEffect(() => {
    setIsCollectionInMyData(
      context.productsAdded?.products?.includes(
        context.selectedProduct?.productName
      )
    );
  }, [context.selectedProduct, context.productsAdded.products]);

  const isUserNotLogged: boolean = useMemo(() => {
    return !isAuthDisabled && !context.userLoggedIn.email;
  }, [context.userLoggedIn.email, isAuthDisabled]);

  /**
   * Call Cart-API queries
   * @param queryKey contains the following params:
   * @param user current user
   * @param name selected product name
   * @param mission product dataType
   * @param urn product ID
   * @param quicklook quicklook url
   * @returns saveProducts
   * @returns deleteProducts
   */
  const [saveProducts, deleteProducts, restoreProduct] = useQueries({
    queries: [
      {
        queryKey: [
          'SaveSelectedProducts',
          {
            payload: [
              {
                user: context.userLoggedIn.email,
                name: context.selectedProduct?.productName,
                storage:
                  context.selectedProduct?.collectionDataModel?.general
                    ?.storageMode ?? '',
                downloaded: context.selectedProduct?.checksum,
                mission:
                  context.selectedProduct?.collectionDataModel?.productDetails[
                    DATASET
                  ] ?? '',
                urn: context.selectedProduct?.id,
                quicklook:
                  context.selectedProduct?.thumbnail ??
                  context.selectedProduct?.quicklook,
                checksum: context.selectedProduct?.checksum,
                endpointURL: context.selectedProduct?.endpointURL,
                href: context.selectedProduct?.href,
                assets: context.selectedProduct?.assets ?? [],
              },
            ],
          },
          navigate,
          context.setError400,
        ],
        queryFn: saveSelectedProducts,
        enabled: false,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        retry: false,
      },
      {
        queryKey: [
          'DeleteSavedProduct',
          {
            payload: [
              {
                user: context.userLoggedIn.email,
                name: context.selectedProduct?.productName,
                downloaded: context.selectedProduct?.checksum,
                mission:
                  context.selectedProduct?.collectionDataModel?.productDetails[
                    DATASET
                  ],
                urn: context.selectedProduct?.id,
                quicklook:
                  context.selectedProduct?.thumbnail ??
                  context.selectedProduct?.quicklook,
              },
            ],
          },
        ],
        queryFn: deleteSavedProduct,
        enabled: false,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        retry: false,
      },
      {
        queryKey: [
          'RestoreArchivedProduct',
          {
            payload: {
              product: context.selectedProduct?.productName,
              process: 'download',
            },
          },
        ],
        queryFn: restoreArchivedProduct,
        enabled: false,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        retry: false,
      },
    ],
  });

  /**
   * Toggle Button status on user click.
   * @returns void
   */
  const addOrRemoveProduct = () => {
    if (isUserNotLogged) return;
    let productsToAdd: ProductsAdded;
    if (!isCollectionInMyData) {
      productsToAdd = {
        products: [
          ...context.productsAdded?.products,
          context.selectedProduct?.productName,
        ],
        results: [
          ...context.productsAdded?.results,
          context.selectedProduct?.productName,
        ],
        levelSet: context.productsAdded.levelSet,
        satelliteSet: context.productsAdded.satelliteSet,
        collectionsDetails: [
          ...context.productsAdded.collectionsDetails,
          context.selectedProduct,
        ],
      };
      if (productsToAdd?.products?.length <= 100) {
        saveProducts.refetch();
        messageDisplay(
          [context.selectedProduct?.productName]?.length,
          'Data ADDED'
        );

        if (!storageMode) {
          restoreProduct.refetch();
        }
      }
    } else {
      productsToAdd = {
        products: context.productsAdded?.products.filter(
          (el) => el !== context.selectedProduct?.productName
        ),
        results: context.productsAdded.results.filter(
          (el) => el !== context.selectedProduct?.productName
        ),
        levelSet: context.productsAdded.levelSet,
        satelliteSet: context.productsAdded.satelliteSet,
        collectionsDetails: context.productsAdded.collectionsDetails.filter(
          (el) => el.productName !== context.selectedProduct?.productName
        ),
      } as ProductsAdded;
      if (productsToAdd?.products?.length < 100) {
        deleteProducts.refetch();
        messageDisplay(
          context.productsAdded?.products?.length -
            productsToAdd?.products?.length,
          'Data REMOVED'
        );
      }
    }
    if (productsToAdd?.products?.length <= 100) {
      context.setProductsAdded(productsToAdd);
      setIsCollectionInMyData(!isCollectionInMyData);
    } else {
      setMaxProductsWarningVisible(true);
    }
  };

  const { refetch: refetchQuotasDownload } = useQuery({
    queryKey: ['QuotasDownload', navigate, context.setError400],
    queryFn: getDownloadQuotas,
    enabled: false, // This prevents the query from automatically running
  });

  /**
   * prepare Download Mode
   * @returns isSingleFiledownloadMode boolean
   */
  const prepareDownloadMode = () => {
    let isSingleFiledownloadMode = true;
    const assetsFileTypeExclusionList = prepareExclusionList();

    const dataRoleAssetsList = context.selectedProduct?.assets?.filter(
      (asset) =>
        asset.roles.includes('data') &&
        assetsFileTypeExclusionList.every(
          (typeExclusion) => asset.type !== typeExclusion
        )
    );

    if (dataRoleAssetsList?.length > 1) {
      isSingleFiledownloadMode = false;
    }

    return isSingleFiledownloadMode;
  };

  /**
   * prepare exclusion list of extentions
   * @returns exclusion list of extentions
   */
  const prepareExclusionList = () => {
    // Application get list of exclusion files type from runtime var.
    const assetsFileTypeExclusionString =
      process.env.NODE_ENV === 'production'
        ? window.__RUNTIME_CONFIG__?.ASSETS_FILE_TYPE_EXCLUSION
        : 'application/xml,application/json';
    // transform into list object without spacing.
    return assetsFileTypeExclusionString.replace(' ', '').split(',');
  };

  /**
   * prepare Multiple Assets To Download
   * @returns assets With No Exclusion Types list
   */
  const prepareMultipleAssetsToDownload = () => {
    const assetsFileTypeExclusionList = prepareExclusionList();

    const assetsWithNoExclusionTypes = context.selectedProduct.assets
      .filter((element) => !assetsFileTypeExclusionList.includes(element.type))
      .map((asset) => {
        return {
          href: asset.href,
          title: asset.title,
          checked: false,
          productId: context.selectedProduct.id,
        } as unknown as AssetWithProductId;
      });

    return assetsWithNoExclusionTypes;
  };

  /**
   * download Product as zip file.
   * @returns void
   */
  const downloadProduct = (e: any) => {
    e.stopPropagation();
    if (isUserNotLogged) return;
    setIsLoading([
      ...isLoading,
      { showSpinner: true, productId: context.selectedProduct?.id },
    ]);
    refetchQuotasDownload().then((quotasDownload) => {
      const messageQuotas = (
        <React.Fragment>
          <p>
            Your request exceeds the amount of data you are allowed to download
            at once. Please reduce the scope of your download to a maximum of
            <span className="quotasDownload">
              {quotasDownload.data?.remainingQuota}
            </span>
            products or try again later. You can consult your detailed quota in
            My Profile
          </p>
        </React.Fragment>
      );
      if (quotasDownload.data?.remainingQuota === 0) {
        confirmDialog({
          message: messageQuotas,
          header: 'Download cannot proceed',
          headerStyle: { color: 'red' },
          acceptClassName: 'AcceptBtn',
          rejectClassName: 'RejectBtn',
        });
      }
    });
    if (!storageMode) {
      confirmDialog({
        message:
          'The data you are trying to download is currently archived. Please click on [Add to My Data] to start its recovery.',
        header: 'Download cannot proceed',
        headerStyle: { color: 'red' },
        acceptClassName: 'AcceptBtn',
        rejectClassName: 'RejectBtn',
      });
      setIsLoading([
        ...isLoading.splice(
          isLoading.findIndex(
            (loadingElem) =>
              loadingElem.productId === context.selectedProduct?.id
          ),
          1
        ),
      ]);
    } else {
      // We evaluate if there is more than one asset with the data role in selected product
      const isSingleFiledownloadMode = prepareDownloadMode();

      if (isSingleFiledownloadMode) {
        context.setDownloadingProducts(context.selectedProduct?.id);
        const productName = context.selectedProduct?.productName;
        const safeAssetTitle =
          context.selectedProduct?.assets &&
          context.selectedProduct.assets.length > 0
            ? context.selectedProduct.assets[0].title
            : undefined;

        // Regex pour extension composée à la fin (ex: .tar.gz)
        const compoundExtRegex = /(\.[^.]+\.[^.]+)$/;
        // Regex pour extension simple à la fin (ex: .zip)
        const simpleExtRegex = /(\.[^.]+)$/;

        let finalFileName = productName;
        let productNameExtension = '';
        if (productName) {
          // Cherche une extension composée sur productName
          const matchCompound = productName.match(compoundExtRegex);
          if (matchCompound) {
            productNameExtension = matchCompound[1];
          } else {
            // Cherche une extension simple sur productName
            const matchSimple = productName.match(simpleExtRegex);
            if (matchSimple) {
              productNameExtension = matchSimple[1];
            }
          }
        }

        if (!productNameExtension && safeAssetTitle) {
          // Cherche extension composée sur asset[0].title
          const matchCompoundAsset = safeAssetTitle.match(compoundExtRegex);
          let assetExtension = '';
          if (matchCompoundAsset) {
            assetExtension = matchCompoundAsset[1];
          } else {
            // Cherche extension simple sur asset[0].title
            const matchSimpleAsset = safeAssetTitle.match(simpleExtRegex);
            if (matchSimpleAsset) {
              assetExtension = matchSimpleAsset[1];
            }
          }
          if (assetExtension) {
            finalFileName = productName + assetExtension;
          }
        }

        downloadProductZipFile(
          context.selectedProduct?.id,
          context.selectedProduct?.checksum,
          finalFileName,
          context.setError400,
          setDownloadProgress,
          navigate,
          false
        ).finally(() => {
          context.setDownloadingProducts(context.selectedProduct?.id);
          setIsLoading([
            ...isLoading.splice(
              isLoading.findIndex(
                (loadingElem) =>
                  loadingElem.productId === context.selectedProduct?.id
              ),
              1
            ),
          ]);
        });
      } else {
        // Get all assets that have role type and not in exclusion list to show user the dialog download choices.
        setAssetsToShow(prepareMultipleAssetsToDownload());
        // Show Dialog to ask user to choose assets to download
        setShowDialog(true);
      }
    }
  };

  /**
   * Launch downloads on multiple assets when user select them is Dialog.
   */
  useEffect(() => {
    if (assetsToDownload.length) {
      // Download assets selected
      context.setDownloadingProducts(context.selectedProduct?.id);
      Promise.all(
        assetsToDownload.map((asset) =>
          downloadProductZipFile(
            context.selectedProduct?.id,
            asset.href.split('files/')[1],
            asset.title,
            context.setError400,
            setDownloadProgress,
            navigate,
            true
          )
        )
      ).finally(() => {
        context.setDownloadingProducts(context.selectedProduct?.id);
        setIsLoading([
          ...isLoading.splice(
            isLoading.findIndex(
              (loadingElem) =>
                loadingElem.productId === context.selectedProduct?.id
            ),
            1
          ),
        ]);
      });
    }
    // eslint-disable-next-line
  }, [assetsToDownload]);

  return (
    <div className="collection-action-button">
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />
      <ShowFilesToDownloadDialog
        assetsFromProduct={assetsToShow}
        setAssetsToDownload={setAssetsToDownload}
        showDialog={showDialog}
        setShowDialog={setShowDialog}
      />
      <Button
        icon={
          isDatalakeAvailable === 'unavailable' && !isUserNotLogged
            ? 'pi pi-exclamation-triangle'
            : isCollectionInMyData
            ? 'pi pi-check'
            : !isUserNotLogged
            ? 'pi pi-plus'
            : null
        }
        iconPos="right"
        label={
          isCollectionInMyData ? 'Already added to My Data' : 'Add to My Data'
        }
        className={`button-add ${isUserNotLogged ? 'disabled' : ''} ${
          isCollectionInMyData
            ? 'p-button-success p-button-sm'
            : 'p-button-warning p-button-sm button-warning-border'
        } ${
          isDatalakeAvailable === 'unavailable' ||
          isDatalakeAvailable === 'loading'
            ? 'button-no-datalake'
            : null
        } `}
        onClick={() => {
          if (!isUserNotLogged && isDatalakeAvailable === 'available') {
            addOrRemoveProduct();
          }
        }}
        tooltipOptions={{ position: 'top' }}
        tooltip={getTooltipMessage(isUserNotLogged, false, isDatalakeAvailable)}
      />
      <Button
        icon={
          isDatalakeAvailable === 'unavailable' && !isUserNotLogged
            ? 'pi pi-exclamation-triangle'
            : null
        }
        iconPos="right"
        label="Download"
        className={`button-download ${
          isUserNotLogged
            ? 'disabled'
            : 'p-button-warning p-button-sm button-warning-border'
        } ${
          isDatalakeAvailable === 'unavailable' ||
          isDatalakeAvailable === 'loading'
            ? 'button-no-datalake'
            : null
        } `}
        onClick={(e) => {
          if (!isUserNotLogged && isDatalakeAvailable === 'available') {
            downloadProduct(e);
          }
        }}
        tooltipOptions={{ position: 'top' }}
        tooltip={getTooltipMessage(isUserNotLogged, true, isDatalakeAvailable)}
        loading={
          context.downloadingProducts?.includes(context.selectedProduct?.id)
            ? isLoading.find(
                (loadingElem) =>
                  loadingElem.productId === context.selectedProduct?.id
              )?.showSpinner
            : false
        }
      />
    </div>
  );
};
